import React, { useMemo, useState } from 'react';
import { Collapse, Button, Dropdown } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingOutlined, AccountBookOutlined, SwapOutlined, DatabaseOutlined,
  FormOutlined, WalletOutlined, CreditCardOutlined, CalculatorOutlined,
  TeamOutlined, ClockCircleOutlined, DollarOutlined, ExperimentOutlined,
  SafetyCertificateOutlined, AlertOutlined, GiftOutlined, FolderOpenOutlined,
  NotificationOutlined, ToolOutlined, RightOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined,
  BugOutlined, ClockCircleFilled, CheckCircleFilled, MoreOutlined,
  StarOutlined, StarFilled,
} from '@ant-design/icons';

type ModuleTestStatus = 'untested' | 'has_bug' | 'bug_fixed' | 'verified';

type ERPModule = {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
};

const STATUS_ICON: Record<ModuleTestStatus, { icon: React.ReactNode; label: string }> = {
  untested:  { icon: <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#d9d9d9' }} />, label: '未測試' },
  has_bug:   { icon: <BugOutlined style={{ color: '#cf1322', fontSize: 12 }} />, label: '待修復' },
  bug_fixed: { icon: <ClockCircleFilled style={{ color: '#d48806', fontSize: 12 }} />, label: '待驗收' },
  verified:  { icon: <CheckCircleFilled style={{ color: '#389e0d', fontSize: 12 }} />, label: '已完成' },
};

interface Props {
  modules: ERPModule[];
  searchText?: string;
  getModuleStatus?: (m: ERPModule) => ModuleTestStatus;
  onStatusChange?: (key: string, status: ModuleTestStatus) => void;
  favorites?: Set<string>;
  onToggleFavorite?: (key: string) => void;
  onNavigate?: () => void;
}

const FORM_ID_OVERRIDES: Record<string, string> = {
  sc: 'pwc202f1',
  c5m5: 'pwc5m5f1',
};

const extractFormId = (m: ERPModule): string | null => {
  if (FORM_ID_OVERRIDES[m.key]) return FORM_ID_OVERRIDES[m.key];
  const match = m.description.match(/\(([^()]+)\)\s*$/);
  return match ? match[1].trim() : null;
};

const CATEGORIES: { id: string; name: string; icon: React.ReactNode; keys: string[] }[] = [
  // 1. 銷售 Sales
  {
    id: 'sales', name: '銷售', icon: <ShoppingOutlined style={{ color: '#1677ff' }} />,
    keys: [
      'sc', 'pwc201f1', 'c204', 'c205', 'c208', 'c209', 'c212', 'shipping-order',
      'c304', 'c305', 'c322', 'c324', 'c330', 'c358', 'pwc301', 'pwc315',
      'c502', 'c504', 'c514', 'c516', 'c526',
      'c5m2', 'c5m4', 'c5m5', 'c5n4', 'c5n6', 'pwc505', 'pwc512', 'pwr5c4',
    ],
  },
  // 2. 採購 Purchase
  {
    id: 'purchase', name: '採購', icon: <AccountBookOutlined style={{ color: '#531dab' }} />,
    keys: [
      'd103', 'd201', 'd202', 'd203', 'd204', 'd205', 'd206', 'd214',
      'pwd210', 'pwd216', 'pwd217', 'warehouse-return-stats',
      'd301', 'd302', 'd303', 'd304', 'd305', 'd306', 'd309', 'd312', 'd313', 'd314',
      'd319', 'd320', 'd324', 'd328', 'pwd307', 'pwd308', 'pwd310',
      'd501', 'd502', 'd504', 'd506', 'pwd503', 'pwd512',
      'd5m1', 'd5m2', 'd5m3', 'd5m4', 'r5d2', 'r5d5', 'r5d6', 'r211',
    ],
  },
  // 3. 倉庫入出庫
  {
    id: 'warehouse', name: '倉庫入出庫', icon: <SwapOutlined style={{ color: '#08979c' }} />,
    keys: ['pw2203', 'pw2204f1', 'pw220a', 'pw2501', 'outbound-notice'],
  },
  // 4. 庫存管理
  {
    id: 'inventory', name: '庫存管理', icon: <DatabaseOutlined style={{ color: '#0958d9' }} />,
    keys: [
      'inv-move-cat', 'm106', 'inv-reason', 'inv-process', 'warehouse-location',
      'm201', 'm207', 'm209', 'pwm202', 'pwm211', 'consignment-ship',
      'm301', 'm302', 'm303', 'm305', 'm309', 'm310',
      'm501', 'm5m1', 'm5m3', 'm5m8', 'r5m3', 'r5m8',
    ],
  },
  // 5. 基礎資料 Master Data
  {
    id: 'master', name: '基礎資料', icon: <FormOutlined style={{ color: '#389e0d' }} />,
    keys: [
      'product-grade', 'pw1115f1', 'company', 'dept', 'product',
      'trade-terms', 'quality-req', 'material', 'material-report',
      'payment-terms', 'color-code', 'color-report', 'currency-report',
      'processing-fee', 'proc-quality', 'pack-customer', 'business-card',
      'vendor-eval', 'remark', 'insp-method', 'test-item', 'exchange-rate',
      'pw150h', 'pw150k', 'p15mh', 'p15mj',
      'pw1312', 'pw1313', 'pw1315', 'p1304', 'p131k', 'dept-report',
    ],
  },
  // 6. 應收帳款
  {
    id: 'ar', name: '應收帳款', icon: <WalletOutlined style={{ color: '#d4380d' }} />,
    keys: [
      'g204', 'g303', 'g305', 'g306', 'g319',
      'pwg201', 'pwg208', 'pwg302', 'pwg304', 'pwg332',
      'pwg501', 'pwg504', 'pwg5m1', 'pwg5m5', 'pwg5m4', 'pwg5m8',
      'receivable-detail', 'invoice-request',
    ],
  },
  // 7. 應付帳款
  {
    id: 'ap', name: '應付帳款', icon: <CreditCardOutlined style={{ color: '#c41d7f' }} />,
    keys: ['pwh203', 'pwh204', 'h301', 'h302', 'h303', 'h311', 'h312', 'pwh5m8'],
  },
  // 8. 會計傳票
  {
    id: 'accounting', name: '會計傳票', icon: <CalculatorOutlined style={{ color: '#876800' }} />,
    keys: ['accounting-voucher', 'fin-billdata'],
  },
  // 9. 人事 HR
  {
    id: 'hr', name: '人事', icon: <TeamOutlined style={{ color: '#08979c' }} />,
    keys: [
      'pw910cf1', 'employee', 'pw9201', 'pw9204f1', 'pw9205', 'p9207', 'p9209', 'pw9208',
      'emp-group', 'emp-shortcode', 'dept-staffing', 'proxy-setting',
      'job-title', 'job-grade', 'dept-major', 'bonus-def',
      'pw9403', 'pw9404', 'pw9701', 'pw98m2', 'pw98m5',
      'p9301', 'p9306', 'pw9304', 'pw930b',
    ],
  },
  // 10. 考勤/出勤
  {
    id: 'attendance', name: '考勤/出勤', icon: <ClockCircleOutlined style={{ color: '#d46b08' }} />,
    keys: [
      'pw9210f1', 'pw9213', 'pw9220', 'pw9221f1', 'pw9224', 'pw9236f1', 'pw9119',
      'attendance-status', 'work-schedule', 'vp-approval-priority', 'holiday', 'leave-type',
      'pw9614', 'p9513',
      'p9310', 'p9311', 'p9315', 'p930h',
      'pw9312', 'pw9314', 'pw9318', 'pw9319', 'pw9321', 'pw9322', 'pw9324', 'pw9411',
    ],
  },
  // 11. 薪資
  {
    id: 'payroll', name: '薪資', icon: <DollarOutlined style={{ color: '#cf1322' }} />,
    keys: [
      'pw9531', 'pw9233', 'salary-adjust', 'base-salary', 'casual-payroll',
      'pay-item', 'nhi-params', 'labor-ins', 'pension-params',
      'pw933a', 'pw9334', 'pw9335', 'pw9336', 'pw9337', 'pw9339', 'pw933b', 'pw9341',
    ],
  },
  // 12. 研發 R&D
  {
    id: 'rd', name: '研發', icon: <ExperimentOutlined style={{ color: '#1d39c4' }} />,
    keys: [
      'j201', 'j202', 'j203', 'j207', 'j208', 'j212', 'j214', 'j219', 'j30c',
      'j501', 'j502', 'j514', 'j515', 'j5m1', 'j5m2', 'j5m3', 'j5m8', 'j5n4', 'j5n5', 'j5n8',
      'rd-subcontract', 'new-product-report', 'pwj220',
      'pwj308', 'pwj30a', 'pwj30b', 'pwj310', 'pwj324',
      'pwj503', 'pwj508', 'pwj5m7', 'pwr5j3',
    ],
  },
  // 13. 品管/包裝 QC
  {
    id: 'qc', name: '品管/包裝', icon: <SafetyCertificateOutlined style={{ color: '#7cb305' }} />,
    keys: ['inspection-request', 'p203', 'p503', 'pwp211', 'pwp252', 'pwp253', 'pwp303'],
  },
  // 14. 客訴 Customer Service
  {
    id: 'complaint', name: '客訴', icon: <AlertOutlined style={{ color: '#fa541c' }} />,
    keys: [
      'complaint-desc', 'complaint-cause', 'complaint-req', 'defect-cause',
      'n202', 'n211', 'n301', 'n351', 'n501', 'n502', 'n503', 'n5m3', 'n5p1',
      'pwn201', 'pwn511', 'pwn5ma', 'pwn5n1', 'pwn5mb', 'r5np',
    ],
  },
  // 15. 事務用品
  {
    id: 'office', name: '事務用品', icon: <GiftOutlined style={{ color: '#9254de' }} />,
    keys: ['office-supplies', 'b255'],
  },
  // 16. 文管 ISO
  {
    id: 'iso', name: '文管 ISO', icon: <FolderOpenOutlined style={{ color: '#597ef7' }} />,
    keys: ['doc-setup', 'l202', 'l204', 'l205', 'l303', 'l313', 'l316', 'l402', 'l413', 'pwl304'],
  },
  // 17. 公告/會議
  {
    id: 'announce', name: '公告/會議', icon: <NotificationOutlined style={{ color: '#13c2c2' }} />,
    keys: ['announcement', 'announcement-query', 't202', 't206', 't207', 'pwt205', 't5m2'],
  },
  // 18. 系統管理
  {
    id: 'system', name: '系統管理', icon: <ToolOutlined style={{ color: '#8c8c8c' }} />,
    keys: ['pw0113f1', 'pw0308', 'pw030a', 'access-control'],
  },
];

const defaultGetStatus = (): ModuleTestStatus => 'untested';

const ModuleCategoryView: React.FC<Props> = ({ modules, searchText = '', getModuleStatus = defaultGetStatus, onStatusChange, favorites, onToggleFavorite, onNavigate }) => {
  const navigate = useNavigate();
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const moduleMap = useMemo(() => new Map(modules.map(m => [m.key, m])), [modules]);

  const categorized = useMemo(() => {
    const kw = searchText.trim().toLowerCase();
    return CATEGORIES.map(cat => {
      let mods = cat.keys
        .map(k => moduleMap.get(k))
        .filter((m): m is ERPModule => !!m);
      if (kw) {
        mods = mods.filter(m =>
          m.title.toLowerCase().includes(kw) ||
          m.description.toLowerCase().includes(kw) ||
          m.key.toLowerCase().includes(kw)
        );
      }
      return { ...cat, modules: mods };
    }).filter(cat => cat.modules.length > 0);
  }, [moduleMap, searchText]);

  // Auto-expand all when searching
  const effectiveKeys = searchText.trim()
    ? categorized.map(c => c.id)
    : activeKeys;

  const allExpanded = effectiveKeys.length === categorized.length;

  const toggleAll = () => {
    if (allExpanded) {
      setActiveKeys([]);
    } else {
      setActiveKeys(categorized.map(c => c.id));
    }
  };

  const items = categorized.map(cat => ({
    key: cat.id,
    label: (
      <span style={{ fontWeight: 500, fontSize: 14 }}>
        <span style={{ marginRight: 8 }}>{cat.icon}</span>
        {cat.name}
        <span style={{ color: '#999', fontWeight: 400, marginLeft: 6, fontSize: 13 }}>
          ({cat.modules.length})
        </span>
      </span>
    ),
    children: (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '2px 12px',
        padding: '0 8px',
      }}>
        {cat.modules.map(m => {
          const status = getModuleStatus(m);
          const si = STATUS_ICON[status];
          const isFav = favorites?.has(m.key);
          return (
            <div
              key={m.key}
              style={{
                padding: '5px 10px',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: '#262626',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {onToggleFavorite && (
                <span
                  onClick={e => { e.stopPropagation(); onToggleFavorite(m.key); }}
                  style={{ cursor: 'pointer', flexShrink: 0, lineHeight: 1, fontSize: 12 }}
                  title={isFav ? '取消常用' : '加入常用'}
                >
                  {isFav
                    ? <StarFilled style={{ color: '#faad14' }} />
                    : <StarOutlined style={{ color: '#d9d9d9' }} />}
                </span>
              )}
              <span
                onClick={() => { navigate(m.path); onNavigate?.(); }}
                style={{
                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#1677ff'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#262626'; }}
              >
                <RightOutlined style={{ fontSize: 9, color: '#bfbfbf', flexShrink: 0 }} />
                {m.title}
                {(() => { const fid = extractFormId(m); return fid ? <span style={{ color: '#999', fontSize: 11, marginLeft: 4 }}>({fid})</span> : null; })()}
              </span>
              {onStatusChange ? (
                <Dropdown
                  menu={{
                    items: (['untested', 'has_bug', 'bug_fixed', 'verified'] as ModuleTestStatus[]).map(s => ({
                      key: s,
                      label: (
                        <span onClick={e => { e.stopPropagation(); onStatusChange(m.key, s); }}>
                          {STATUS_ICON[s].icon} <span style={{ marginLeft: 4 }}>{STATUS_ICON[s].label}</span>
                        </span>
                      ),
                    })),
                  }}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <span
                    onClick={e => e.stopPropagation()}
                    style={{ cursor: 'pointer', flexShrink: 0, lineHeight: 1, padding: '2px 4px' }}
                    title={si.label}
                  >
                    {status === 'untested' ? <MoreOutlined style={{ color: '#bbb', fontSize: 12 }} /> : si.icon}
                  </span>
                </Dropdown>
              ) : status !== 'untested' && (
                <span style={{ flexShrink: 0, lineHeight: 1, padding: '2px 4px' }} title={si.label}>
                  {si.icon}
                </span>
              )}
            </div>
          );
        })}
      </div>
    ),
  }));

  return (
    <div>
      <div style={{ marginBottom: 12, textAlign: 'right' }}>
        <Button
          size="small"
          type="text"
          icon={allExpanded ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
          onClick={toggleAll}
        >
          {allExpanded ? '收合全部' : '展開全部'}
        </Button>
      </div>
      <Collapse
        items={items}
        activeKey={effectiveKeys}
        onChange={keys => setActiveKeys(keys as string[])}
        ghost
      />
    </div>
  );
};

export default ModuleCategoryView;
